import { Alert, Divider, Stack, Typography } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { type AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import type { ProductData } from "~/domains/product/types/ProductData";
import ConnectedCPDProgress, {
  getCPDProgressData,
} from "~/features/cms/homepagePersonalization/cpd/components/cpdProgress/ConnectedCPDProgress";
import {
  type AddToCartResultFormData,
  getMultipleEntriesRows,
  shouldRenderMultipleEntriesResult,
} from "./addToCartResultUtils";
import AddToCartResultSingleItem from "./AddToCartResultSingleItem";
import AddToCartResultsMultipleItems from "./AddToCartResultsMultipleItems";

interface AddToCartResultProps {
  formData: AddToCartResultFormData;
  productInfo?: ProductData;
  isTablet: boolean;
  responseItems?: AddToCartResponseItem[];
}

const AddToCartResult = ({ formData, productInfo, isTablet, responseItems }: AddToCartResultProps) => {
  const { t } = useTranslation();
  const phaseOut2Enabled = isPhaseOut2Enabled();
  const hasMultiple = shouldRenderMultipleEntriesResult({ formData, productInfo, responseItems, phaseOut2Enabled });
  const isCPDProgressEnabled = !hasMultiple && window.SCHA.feature.cpdBannerDrawer && !!getCPDProgressData();

  const entries = getMultipleEntriesRows({ formData, productInfo, responseItems, phaseOut2Enabled });
  const totalPieces = entries.reduce((acc, e) => acc + e.quantity, 0);

  return (
    <Stack gap={2}>
      {isCPDProgressEnabled ? (
        <Stack gap={2}>
          <Typography variant="titleLarge">{t("addToCart.successMessage")}</Typography>
          <ConnectedCPDProgress />
          <Divider />
        </Stack>
      ) : hasMultiple ? (
        <Alert severity="success">
          {t("addToCart.multipleProductsAdded", { count: entries.length, pieces: totalPieces })}
        </Alert>
      ) : (
        <Alert severity="success">{t("addToCart.successMessage")}</Alert>
      )}

      {hasMultiple ? (
        <AddToCartResultsMultipleItems entries={entries} />
      ) : (
        <AddToCartResultSingleItem productInfo={productInfo} formData={formData} />
      )}
    </Stack>
  );
};

export default AddToCartResult;
